import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { NAV_GROUPS } from "@/lib/nav";
import { get } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/common/BrandLogo";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";

const NavItem = ({ item, collapsed, onNavigate }) => {
  const { t } = useTranslation();
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.path}
      end={item.end}
      onClick={onNavigate}
      data-testid={`sidebar-nav-item-${item.key}`}
      className={({ isActive }) =>
        cn(
          "group flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors duration-150 og-focus",
          isActive ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
          collapsed && "justify-center px-0",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("h-[18px] w-[18px] shrink-0", isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground")} aria-hidden="true" />
          {!collapsed && <span className="truncate">{t(`nav.${item.key}`)}</span>}
          {!collapsed && !item.ready && (
            <span className="ml-auto rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t("nav.soon")}</span>
          )}
        </>
      )}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" className="bg-card text-foreground">{t(`nav.${item.key}`)}{!item.ready ? ` · ${t("nav.soon")}` : ""}</TooltipContent>
    </Tooltip>
  );
};

/** Shared sidebar body, used by the desktop rail and the mobile sheet. */
export const SidebarContent = ({ collapsed = false, onToggle, onNavigate }) => {
  const { t } = useTranslation();
  const { orgId, currentOrg } = useAuth();
  const { data: brain } = useQuery({ queryKey: ["brand-brain", orgId], queryFn: () => get("/brand-brain"), enabled: !!orgId, staleTime: 30_000 });
  const percent = brain?.completeness_score ?? null;

  return (
    <div className="flex h-full flex-col" data-testid="app-sidebar">
      <div className={cn("flex h-14 items-center border-b border-border px-4", collapsed && "justify-center px-0")}>
        <BrandLogo compact={collapsed} />
      </div>
      <nav className="og-scroll flex-1 overflow-y-auto px-3 py-4" aria-label={t("nav.menu")}>
        {NAV_GROUPS.map((group) => (
          <div key={group.key} className="mb-5">
            {!collapsed && <div className="og-section-label mb-2 px-3">{t(`nav.groups.${group.key}`)}</div>}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem key={item.key} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        {!collapsed && currentOrg && (
          <NavLink to="/app/brand-brain" onClick={onNavigate} className="og-focus block rounded-lg border border-border bg-background p-3 transition-colors duration-150 hover:bg-muted" data-testid="sidebar-brand-brain-card">
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-foreground">{t("nav.brandBrain")}</span>
              <span className="text-muted-foreground" data-testid="sidebar-brand-brain-percent">{percent === null ? "…" : t("common.percent", { value: percent })}</span>
            </div>
            <Progress value={percent ?? 0} className="h-1.5" aria-label={t("brandBrain.completeness")} />
            <div className="mt-1.5 truncate text-[11px] text-muted-foreground">{currentOrg.name}</div>
          </NavLink>
        )}
        {onToggle && (
          <Button variant="ghost" size="sm" onClick={onToggle} className={cn("mt-2 w-full justify-start text-muted-foreground", collapsed && "justify-center px-0")} data-testid="sidebar-collapse-toggle" aria-label={collapsed ? t("nav.expand") : t("nav.collapse")}>
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            {!collapsed && <span className="ml-2 text-xs">{t("nav.collapse")}</span>}
          </Button>
        )}
      </div>
    </div>
  );
};
